import { CloudDownload, Delete, Visibility } from "@material-ui/icons";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import Button from "components/Forms/CustomButtons/ButtonTS";
import CustomPopover, {
  CustomPopoverRef,
} from "components/UI/CustomPopover/CustomPopover";
import PopoverButton from "components/UI/CustomPopover/PopoverButton";
import Table from "components/Tables/ReactTable/ReactTable";
import UploadDocumentModal from "components/Modals/UploadDocumentModal";
import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ActivityItemConfig } from "services/input-core/activity-items/@types";
import { useActivityItemConfig } from "services/input-core/activity-items/get-activity-item-config";
import { DocumentAttachment } from "services/input-core/document-attachment/@types";
import { useDeleteDocumentAttachment } from "services/input-core/document-attachment/delete-document-attachment";
import { useFinishAttachmentUpload } from "services/input-core/document-attachment/finish-attachment-upload";
import { useGetPaginatedDocumentAttachments } from "services/input-core/document-attachment/get-paginated-document-attachments";
import { ActivityItemValidationStatusEnum } from "utils/@types";
import {
  checkMessageErrors,
  compareStringsIgnoreCaseAndRemoveSpecialChars,
} from "utils/functions";
import ConfirmDeleteModal from "../ConfirmDeleteModal";
import ConfirmUploadComplete from "../ConfirmUploadComplete";
import { toast } from "react-toastify";
import { useBlobToken } from "services/blob/get-token";
import { useActivityItemContext } from "store/activityItem";

interface UploadModalProps {
  profile: "WORKER" | "SOW";
  modalOpen: boolean;
  fieldglassId?: string;
  onClose: () => void;
  uploadType?: boolean;
  setUploadType?: React.Dispatch<React.SetStateAction<boolean>>;
}

async function openImageInNewTab(
  fileUri: string,
  doc: DocumentAttachment,
  notificationCallback: () => void,
) {
  if (!fileUri) return;

  if (fileUri) {
    notificationCallback();
    const { data } = await axios.get(fileUri, {
      responseType: "blob",
    });

    const metadata = {
      type: doc?.mimeType,
      disposition: "inline",
    };

    const file = new File([data], "file", metadata);

    const fileURL = URL.createObjectURL(file);

    window.open(fileURL);
  }
}

const ActionButtons: React.FC<{
  fileUri: string;
  currentFile?: unknown;
  doc?: DocumentAttachment;
  disabled?: boolean;
  handleDelete: () => void;
  notificationCallback: () => void;
  key: number;
}> = ({ notificationCallback, fileUri, handleDelete, doc, key }) => {
  const { t } = useTranslation("modals");
  const { mutate: blobToken, isLoading: isLoadingBlobToken } = useBlobToken();

  const customPopoverRef = useRef<CustomPopoverRef>(null);

  const closePopoverFromParent = () => {
    if (customPopoverRef.current) {
      customPopoverRef.current.closePopover();
    }
  };

  return (
    <div
      style={{
        display: "flex",
        width: "100%",
        justifyContent: "flex-end",
        alignItems: "flex-end",
      }}
    >
      <CustomPopover
        ref={customPopoverRef}
        identifier={`upload-content-kuo`}
        data-cy={`upload-content-popover-${key}`}
      >
        <PopoverButton
          disabled={!fileUri || isLoadingBlobToken}
          color="primary"
          data-cy={`preview-button`}
          onClick={() => {
            closePopoverFromParent();
            blobToken(fileUri, {
              onSuccess: (res: { data: { acessUri: string } }) => {
                openImageInNewTab(res.data.acessUri, doc, notificationCallback);
              },
              onError: () => {
                toast.error(t("get-blob-token-error", { ns: "list-errors" }));
              },
            });
          }}
        >
          <Visibility />
          {t("preview")}
        </PopoverButton>
        <PopoverButton
          disabled={!fileUri || isLoadingBlobToken}
          color="primary"
          data-cy={`download-button`}
          onClick={() => {
            closePopoverFromParent();
            blobToken(fileUri, {
              onSuccess: (res: { data: { acessUri: string } }) => {
                window.open(res.data.acessUri, "_blank", "noopener,noreferrer");
              },
              onError: () => {
                toast.error(t("get-blob-token-error", { ns: "list-errors" }));
              },
            });
          }}
        >
          <CloudDownload />
          {t("download")}
        </PopoverButton>
        <PopoverButton
          data-cy={`delete-button`}
          onClick={() => {
            closePopoverFromParent();
            handleDelete();
          }}
        >
          <Delete style={{ fontSize: "1.2rem" }} />
          {t("delete-document")}
        </PopoverButton>
      </CustomPopover>
    </div>
  );
};

export default function UploadModalContentKuo({
  onClose,
  profile,
  fieldglassId,
  uploadType,
  setUploadType,
}: UploadModalProps) {
  const { t } = useTranslation(["modals", "list-errors", "list-messages"]);

  const { currentActivityItem } = useActivityItemContext((state) => ({
    currentActivityItem: state.currentActivity,
  }));

  const [onUploadSuccessValue, setOnUploadSuccessValue] = React.useState(false);

  const columns: {
    Header: string;
    accessor: string;
  }[] = [
    {
      Header: t("react-table-column-name"),
      accessor: "name",
    },
    {
      Header: t("react-table-column-uploaded-by"),
      accessor: "uploadedBy",
    },
    {
      Header: t("react-table-column-uploaded-at"),
      accessor: "uploadedAt",
    },
    {
      Header: t("react-table-column-actions"),
      accessor: "actions",
    },
  ];

  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();

  const refId = fieldglassId ?? currentActivityItem.fieldglassId;

  const [
    currentConfigForActivityItem,
    setCurrentConfigForActivityItem,
  ] = React.useState<ActivityItemConfig>();

  const [uploadModal, setUploadModal] = React.useState(false);
  const [completeModal, setCompleteModal] = React.useState(false);
  const [deleteModal, setDeleteModal] = React.useState(false);
  const [selectedForDelete, setSelectedForDelete] = React.useState<{
    id: string;
    name: string;
  }>(null);

  const {
    data: documentsFromActivityData,
    refetch: refetchDocumentsFromActivityData,
  } = useGetPaginatedDocumentAttachments(
    {
      active: true,
      zID: currentActivityItem?.zIdentifier,
      fieldglassId: currentActivityItem?.fieldglassId || fieldglassId,
      params: {
        limit: 10,
        page: 1,
      },
    },
    { enabled: onUploadSuccessValue && !uploadType },
  );

  const {
    mutate: deleteAttachment,
    isLoading: isDeletingAttachment,
  } = useDeleteDocumentAttachment();

  const {
    mutate: finishUploadAttachments,
    isLoading: isFinishingUploadAttachments,
  } = useFinishAttachmentUpload();

  const { data: activityItemConfig } = useActivityItemConfig();

  useEffect(() => {
    if (activityItemConfig)
      setCurrentConfigForActivityItem(
        activityItemConfig.find(
          (element) =>
            element.mapping === currentActivityItem?.activityItemCode,
        ),
      );
  }, [activityItemConfig]);

  const isWaitingDownload = () => {
    if (
      compareStringsIgnoreCaseAndRemoveSpecialChars(
        currentActivityItem?.validationStatus,
        ActivityItemValidationStatusEnum.WAITING_FOR_DOWNLOAD,
      )
    ) {
      const roles =
        currentConfigForActivityItem?.validationSteps[
          currentActivityItem?.validationStep
        ]?.executedByRole;

      //Se só tem o supplier como role, então a conta não pode ser buyer
      if (roles?.length === 1 && roles?.includes("supplier")) {
        return (
          keycloak.hasResourceRole("supplier", "uverify") &&
          !keycloak.hasResourceRole("buyer", "uverify")
        );
      }

      let hasRole = false;

      roles?.forEach((role) => {
        hasRole = hasRole || keycloak.hasResourceRole(role, "uverify");
      });

      return hasRole;
    }

    return false;
  };

  const dataFormatted =
    documentsFromActivityData?.docs?.map((doc, index) => ({
      name: doc.name,
      uploadedBy: `${doc?.createdBy?.name} (${doc?.createdBy?.email})`,
      uploadedAt: new Date(doc.createdAt).toLocaleString(),
      fromApi: true,
      actions: (
        <ActionButtons
          fileUri={doc.uri}
          doc={doc}
          key={index}
          notificationCallback={() =>
            toast.info(t("info-open-preview", { ns: "list-messages" }))
          }
          handleDelete={() => {
            setSelectedForDelete({
              id: doc._id,
              name: doc.name,
            });

            setDeleteModal(true);
          }}
        />
      ),
    })) || [];

  const handleDelete = (attachmentId: string) => {
    deleteAttachment(attachmentId, {
      onSuccess: () => {
        toast.success(t("success-deleted-attachment", { ns: "list-messages" }));
        refetchDocumentsFromActivityData();
        setDeleteModal(false);
      },
      onError: (error: any) => {
        toast.error(
          t(
            checkMessageErrors(
              error?.response?.data?.error,
              "ERR_DELETING_ATTACHMENT_DEFAULT",
            ),
            { ns: "list-errors" },
          ),
        );
      },
    });
  };

  const handleFinishAttachments = () => {
    toast.info(t("finishing-upload-step"));

    finishUploadAttachments(
      {
        zIdentifier: currentActivityItem?.zIdentifier,
        activityItemCode: currentActivityItem?.activityItemCode,
        fieldglassId: refId,
        subscriptionId: currentActivityItem?.subscription,
      },
      {
        onSuccess: () => {
          toast.success(t("success", { ns: "list-messages" }));
          queryClient.invalidateQueries([
            profile == "SOW"
              ? "activity-items-sow"
              : profile == "WORKER" && "activity-items-worker",
            refId,
          ]);
          onClose();
        },
        onError: (error: any) => {
          toast.error(
            t(
              checkMessageErrors(
                error?.response?.data?.error,
                "ERR_SAVE_DEFAULT",
              ),
              { ns: "list-errors" },
            ),
          );
        },
      },
    );
  };

  const isButtonsDisabled =
    isDeletingAttachment || isFinishingUploadAttachments;

  const onUploadSuccess = (value: any) => {
    setOnUploadSuccessValue(value);
  };

  return (
    <>
      <ConfirmDeleteModal
        isDeleting={isDeletingAttachment}
        modalOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onConfirm={() => handleDelete(selectedForDelete?.id)}
        fileName={selectedForDelete?.name}
      />

      <ConfirmUploadComplete
        isUploading={isFinishingUploadAttachments}
        modalOpen={completeModal}
        onClose={() => setCompleteModal(false)}
        onConfirm={handleFinishAttachments}
      />

      <UploadDocumentModal
        modalOpen={uploadModal}
        onClose={() => {
          setUploadModal(false);
          refetchDocumentsFromActivityData();
        }}
        profile={profile}
        fieldglassId={fieldglassId ?? refId}
        setUploadType={setUploadType}
        onUploadSuccess={onUploadSuccess}
      />
      <Table columns={columns} data={dataFormatted} initialPageSize={5} />

      <div
        style={{
          display: "flex",
          width: "100%",
          justifyContent: "flex-end",
          gap: "8px",
          marginTop: "24px",
          marginBottom: "24px",
        }}
      >
        <Button
          color="primary"
          data-cy={`add-file-modal-button`}
          onClick={() => setUploadModal(true)}
          disabled={isButtonsDisabled || isWaitingDownload()}
        >
          {t("add-files")}
        </Button>

        <Button
          color="primary"
          onClick={() => setCompleteModal(true)}
          disabled={
            isButtonsDisabled ||
            isWaitingDownload() ||
            dataFormatted.length === 0
          }
        >
          {t("complete-upload")}
        </Button>
      </div>
    </>
  );
}
